import { PatientFormData } from '../hooks/usePatientForm';

export interface LaravelPatient {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  father_name?: string;
  mother_name?: string;
  birth_date: string;
  age: number;
  gender: 'male' | 'female';
  blood_type?: string;
  phone: string;
  email?: string;
  address: string;
  emergency_contact: {
    name: string;
    phone: string;
    relationship: string;
  };
  medical_notes?: string;
  allergies?: string[];
  chronic_conditions?: string[];
  clinic_id: number;
  clinic?: {
    id: number;
    name: string;
  };
  primary_doctor_id?: number;
  primary_doctor?: {
    id: number;
    name: string;
    specialty?: string;
  };
  insurance_provider?: string;
  insurance_number?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  // Add these starring fields
  is_starred: boolean;
  star_reason?: string;
  starred_by?: number;
  starred_at?: string;
}

export interface LaravelPaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    per_page: number;
    to: number;
    total: number;
  };
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
}

class PatientService {
  private static BASE_URL = process.env.REACT_APP_API_URL || 'https://dr-bassam-elayoubi.caduceuslb.com/api';

  private static async request(url: string, options: RequestInit = {}): Promise<any> {
    const token = localStorage.getItem('auth_token');
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
      ...options,
    };

    const response = await fetch(`${this.BASE_URL}${url}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return await response.json();
  }

  // ✅ Get single patient by ID
  static async getPatient(id: number): Promise<LaravelPatient> {
    const result = await this.request(`/patients/${id}`);
    return result.data;
  }

  // ✅ Get available clinics
  static async getClinics(): Promise<{ id: number; name: string; address: string; phone: string; email?: string; is_active: boolean }[]> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/clinics`, {
      headers: {
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch clinics');
    }

    return await response.json();
  }

  static async getPatients(params?: {
    search?: string;
    gender?: string;
    city?: string;
    is_active?: boolean;
    is_starred?: boolean;
    page?: number;
    per_page?: number;
    sort_by?: string;
    sort_order?: 'asc' | 'desc';
  }): Promise<LaravelPaginatedResponse<LaravelPatient>> {
    const token = localStorage.getItem('auth_token');
    const queryParams = new URLSearchParams();
    
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, value.toString());
        }
      });
    }

    const url = `${this.BASE_URL}/patients${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    
    const response = await fetch(url, {
      headers: {
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch patients');
    }

    return await response.json();
  }

  static async createPatient(patientData: PatientFormData): Promise<LaravelPatient> {
    const token = localStorage.getItem('auth_token');
    
    const formattedData = {
      first_name: patientData.firstName,
      last_name: patientData.lastName,
      father_name: patientData.fatherName || null,
      mother_name: patientData.motherName || null,
      birth_date: patientData.birthDate,
      gender: patientData.gender,
      blood_type: patientData.bloodType || null,
      phone: patientData.phone,
      email: patientData.email || null,
      address: patientData.address || null,
      emergency_contact: {
        name: patientData.emergencyContactName || null,
        phone: patientData.emergencyContactPhone || null,
        relationship: patientData.emergencyContactRelationship || null
      },
      medical_notes: patientData.medicalNotes || null,
      allergies: patientData.allergies 
        ? patientData.allergies.split(',').map(a => a.trim()).filter(a => a.length > 0)
        : null,
      chronic_conditions: patientData.chronicConditions 
        ? patientData.chronicConditions.split(',').map(c => c.trim()).filter(c => c.length > 0)
        : null,
      primary_doctor_id: patientData.primaryDoctorId || null,
      insurance_provider: patientData.insuranceProvider || null,
      insurance_number: patientData.insuranceNumber || null
      // clinic_id will be set by backend from authenticated user
    };

    const response = await fetch(`${this.BASE_URL}/patients`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(formattedData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Better error handling for validation errors
      if (response.status === 422 && errorData.errors) {
        const validationErrors = Object.values(errorData.errors).flat().join(', ');
        throw new Error(`Validation Error: ${validationErrors}`);
      }
      
      throw new Error(errorData.message || 'Failed to create patient');
    }

    const result = await response.json();
    return result.data; // Laravel returns { message: "", data: patient }
  }

  static async updatePatient(id: number, patientData: Partial<PatientFormData>): Promise<LaravelPatient> {
    const token = localStorage.getItem('auth_token');
    
    // Transform the data to match Laravel's expected format
    const formattedData: any = {};
    
    if (patientData.firstName !== undefined) formattedData.first_name = patientData.firstName;
    if (patientData.lastName !== undefined) formattedData.last_name = patientData.lastName;
    if (patientData.fatherName !== undefined) formattedData.father_name = patientData.fatherName || null;
    if (patientData.motherName !== undefined) formattedData.mother_name = patientData.motherName || null;
    if (patientData.birthDate !== undefined) formattedData.birth_date = patientData.birthDate;
    if (patientData.gender !== undefined) formattedData.gender = patientData.gender;
    if (patientData.bloodType !== undefined) formattedData.blood_type = patientData.bloodType || null;
    if (patientData.phone !== undefined) formattedData.phone = patientData.phone;
    if (patientData.email !== undefined) formattedData.email = patientData.email || null;
    if (patientData.address !== undefined) formattedData.address = patientData.address;
    if (patientData.medicalNotes !== undefined) formattedData.medical_notes = patientData.medicalNotes || null;
    if (patientData.insuranceProvider !== undefined) formattedData.insurance_provider = patientData.insuranceProvider || null;
    if (patientData.insuranceNumber !== undefined) formattedData.insurance_number = patientData.insuranceNumber || null;
    if (patientData.primaryDoctorId !== undefined) formattedData.primary_doctor_id = patientData.primaryDoctorId || null;
    
    // Handle emergency contact
    if (patientData.emergencyContactName !== undefined || 
        patientData.emergencyContactPhone !== undefined || 
        patientData.emergencyContactRelationship !== undefined) {
      formattedData.emergency_contact = {
        name: patientData.emergencyContactName,
        phone: patientData.emergencyContactPhone,
        relationship: patientData.emergencyContactRelationship
      };
    }
    
    // Handle allergies and chronic conditions
    if (patientData.allergies !== undefined) {
      formattedData.allergies = patientData.allergies 
        ? patientData.allergies.split(',').map(a => a.trim()).filter(a => a.length > 0)
        : null;
    }
    
    if (patientData.chronicConditions !== undefined) {
      formattedData.chronic_conditions = patientData.chronicConditions 
        ? patientData.chronicConditions.split(',').map(c => c.trim()).filter(c => c.length > 0)
        : null;
    }
    
    const response = await fetch(`${this.BASE_URL}/patients/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
      body: JSON.stringify(formattedData)
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Better error handling for validation errors
      if (response.status === 422 && errorData.errors) {
        const validationErrors = Object.values(errorData.errors).flat().join(', ');
        throw new Error(`Validation Error: ${validationErrors}`);
      }
      
      throw new Error(errorData.message || 'Failed to update patient');
    }

    const result = await response.json();
    return result.data;
  }

  static async deletePatient(id: number): Promise<void> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/patients/${id}`, {
      method: 'DELETE',
      headers: {
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to delete patient');
    }
  }

  static async getDoctors(): Promise<{ id: number; name: string; specialty?: string }[]> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/users/doctors`, {
      headers: {
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch doctors');
    }

    return await response.json();
  }

  static async getStatistics(): Promise<{
    total_patients: number;
    active_patients: number;
    male_patients: number;
    female_patients: number;
    patients_with_allergies: number;
    patients_with_conditions: number;
  }> {
    const token = localStorage.getItem('auth_token');
    
    const response = await fetch(`${this.BASE_URL}/patients-statistics`, {
      headers: {
        'Accept': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` }),
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || 'Failed to fetch statistics');
    }

    const result = await response.json();
    return result.data;
  }
  // Add these methods to the PatientService class
  static async starPatient(patientId: number, reason?: string): Promise<LaravelPatient> {
    const result = await this.request(`/patients/${patientId}/star`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
    return result.data;
  }

  static async unstarPatient(patientId: number): Promise<LaravelPatient> {
    const result = await this.request(`/patients/${patientId}/star`, {
      method: 'DELETE',
    });
    return result.data;
  }
}

export default PatientService;